import {
  entityModifiers,
  formatMoney,
  type ModerationEntity,
  type ModifierGroup,
  type ModifierOption,
  type Translate,
} from '@/entities/moderation';
import { LOCALE_ORDER, localeLine } from '../../lib/locales';
import { formatFieldValue } from '../../lib/field-labels';

type ModifiersRowLayout = 'spread' | 'grid';

interface ModifiersSectionProps {
  own: ModerationEntity;
  other: ModerationEntity | null | undefined;
  highlight: boolean;
  t: Translate;
  /**
   * Label/value row alignment: both modes render two-column grids; verdicts
   * use the wider label column from the single-column modal designs.
   */
  layout?: ModifiersRowLayout;
}

/**
 * Modifier groups of the product compare column (Figma 584:11839
 * `Модификаторы` block): per group the product name in three locales,
 * the selection-type rows (single/multiple, count, required) and every
 * option with its localized names plus price. Changed values on the
 * pending side highlight in brand orange.
 */
export function ModifiersSection({
  own,
  other,
  highlight,
  t,
  layout = 'spread',
}: ModifiersSectionProps) {
  const ownGroups = entityModifiers(own);
  const otherGroups: ModifierGroup[] = other ? entityModifiers(other) : [];
  // Both columns lay out the union of the groups, so a group (or add-on) one
  // side lacks reads as an empty block of the same shape and rows stay aligned.
  const groups = unionBy(ownGroups, otherGroups, groupKey, emptyGroup);
  if (groups.length === 0) {
    return (
      <div>
        <h4
          className={
            layout === 'grid'
              ? 'text-base leading-[1.4] font-semibold text-black dark:text-foreground'
              : 'text-[14px] leading-[normal] font-semibold tracking-[-0.28px] text-black dark:text-foreground'
          }
        >
          {t('detail.modifiers', 'Модификаторы')}
        </h4>
        <p className="mt-3 text-sm text-muted-foreground">—</p>
      </div>
    );
  }

  return (
    <div>
      <h4
        className={
          layout === 'grid'
            ? 'text-base leading-[1.4] font-semibold text-black dark:text-foreground'
            : 'text-[14px] leading-[normal] font-semibold tracking-[-0.28px] text-black dark:text-foreground'
        }
      >
        {t('detail.modifiers', 'Модификаторы')}
      </h4>
      <div className="mt-3 flex flex-col gap-4">
        {groups.map((group, index) => (
          <ModifierGroupBlock
            key={groupKey(group, index)}
            group={group}
            counterpart={
              other ? findPeer(otherGroups, group, groups.indexOf(group)) : undefined
            }
            absent={!ownGroups.includes(group)}
            highlight={highlight}
            t={t}
            layout={layout}
          />
        ))}
      </div>
    </div>
  );
}

const groupKey = (group: { id?: string | null | undefined }, index: number): string =>
  group.id ?? `#${index}`;

function emptyGroup(source: ModifierGroup): ModifierGroup {
  return { id: source.id ?? null, name: null, options: [] };
}

function emptyOption(source: ModifierOption): ModifierOption {
  return { id: source.id ?? null, name: null };
}

/**
 * `own` followed by the entries only `peer` has (as empty placeholders), each
 * matched by id — or by position when the entity carries no ids.
 */
function unionBy<T extends { id?: string | null | undefined }>(
  own: readonly T[],
  peer: readonly T[],
  keyOf: (item: T, index: number) => string,
  empty: (source: T) => T,
): T[] {
  const result = [...own];
  const known = new Set(own.map(keyOf));
  peer.forEach((item, index) => {
    if (!known.has(keyOf(item, index))) result.push(empty(item));
  });
  return result;
}

function findPeer<T extends { id?: string | null | undefined }>(
  peers: readonly T[],
  item: T,
  index: number,
): T | undefined {
  return item.id == null
    ? peers[index]
    : peers.find((candidate) => candidate.id === item.id);
}

/** Label/value row: fixed two-column grids for compare and verdicts. */
function rowClass(layout: ModifiersRowLayout): string {
  return layout === 'grid'
    ? 'grid w-full grid-cols-2 gap-3'
    : 'grid w-full grid-cols-2 gap-3';
}

/** Value cell: 14px/400 for all label-value pairs. */
function valueClass(layout: ModifiersRowLayout, changed: boolean): string {
  if (layout === 'grid') {
    return changed
      ? 'min-w-0 font-normal break-words text-destructive'
      : 'min-w-0 font-normal break-words text-black dark:text-foreground';
  }
  return changed
    ? 'min-w-0 font-normal break-words text-destructive'
    : 'min-w-0 font-normal break-words text-black dark:text-foreground';
}

/**
 * Row body text: 14px label/value rows on both layouts so every block reads
 * at one scale.
 */
function rowsTextClass(): string {
  return 'text-sm';
}

/** Sub-head (`Название продукта`, `Тип выбора`, `Вариант N`): headings stay semibold. */
function subheadClass(): string {
  return 'text-sm font-semibold text-black dark:text-foreground';
}

function ModifierGroupBlock({
  group,
  counterpart,
  absent,
  highlight,
  t,
  layout,
}: {
  group: ModifierGroup;
  counterpart: ModifierGroup | undefined;
  /** This column lacks the group; it is drawn empty to keep the rows aligned. */
  absent: boolean;
  highlight: boolean;
  t: Translate;
  layout: ModifiersRowLayout;
}) {
  return (
    <div className="flex flex-col gap-3">
      <ModifierGroupName
        group={group}
        counterpart={counterpart}
        highlight={highlight}
        t={t}
        layout={layout}
      />
      <ModifierSelection
        group={group}
        counterpart={counterpart}
        absent={absent}
        highlight={highlight}
        t={t}
        layout={layout}
      />
      {unionBy(group.options, counterpart?.options ?? [], groupKey, emptyOption).map(
        (option, index) => (
          <ModifierOptionBlock
            key={groupKey(option, index)}
            option={option}
            peer={counterpart ? findPeer(counterpart.options, option, index) : undefined}
            index={index}
            highlight={highlight}
            t={t}
            layout={layout}
          />
        ),
      )}
    </div>
  );
}

/** Group name in three locales (`Название продукта` block). */
function ModifierGroupName({
  group,
  counterpart,
  highlight,
  t,
  layout,
}: {
  group: ModifierGroup;
  counterpart: ModifierGroup | undefined;
  highlight: boolean;
  t: Translate;
  layout: ModifiersRowLayout;
}) {
  // All three locales always render (empty reads as `—`).
  return (
    <div className="flex flex-col gap-3">
      <p className={subheadClass()}>
        {t('detail.modifierProductName', 'Название продукта')}
      </p>
      <div className={`flex flex-col gap-3 ${rowsTextClass()} leading-[1.4] font-normal`}>
        {LOCALE_ORDER.map(({ key, labelKey, fallback }) => {
          const value = localeLine(group.name ?? undefined, key);
          const changed =
            highlight && value !== localeLine(counterpart?.name ?? undefined, key);
          return (
            <div key={key} className={rowClass(layout)}>
              <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
                {t(labelKey, fallback)}
              </dt>
              <dd className={valueClass(layout, changed)}>
                {value === '' ? '—' : value}
              </dd>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Selection rules (`Тип выбора` block): single vs multiple from
 * `max_select` (unset reads as a single variant), the variant count and
 * the required flag only when `max_select` / `min_select` are set.
 */
function ModifierSelection({
  group,
  counterpart,
  absent,
  highlight,
  t,
  layout,
}: {
  group: ModifierGroup;
  counterpart: ModifierGroup | undefined;
  absent: boolean;
  highlight: boolean;
  t: Translate;
  layout: ModifiersRowLayout;
}) {
  const max = group.max_select ?? undefined;
  const min = group.min_select ?? undefined;
  const peerMax = counterpart?.max_select ?? undefined;
  const peerMin = counterpart?.min_select ?? undefined;

  const single = (max ?? 1) <= 1;
  const typeChanged = highlight && (absent || (max ?? 1) !== (peerMax ?? 1));
  const countChanged = highlight && max !== peerMax;
  const requiredChanged = highlight && min !== peerMin;
  const dash = '—';

  // All three rows always render — an unset value reads `—` — so both columns
  // keep the same height whether or not the group sets a limit.
  return (
    <div className="flex flex-col gap-3">
      <p className={subheadClass()}>{t('detail.selectionType', 'Тип выбора')}</p>
      <div className={`flex flex-col gap-3 ${rowsTextClass()} leading-[1.4] font-normal`}>
        <div className={rowClass(layout)}>
          <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
            {t('detail.selectionType', 'Тип выбора')}
          </dt>
          <dd className={valueClass(layout, typeChanged)}>
            {absent
              ? dash
              : single
                ? t('detail.singleVariant', 'Один вариант')
                : t('detail.multipleVariants', 'Несколько вариантов')}
          </dd>
        </div>
        <div className={rowClass(layout)}>
          <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
            {t('detail.variantCount', 'Кол-во вариантов')}
          </dt>
          <dd className={`${valueClass(layout, countChanged)} tabular-nums`}>
            {max ?? dash}
          </dd>
        </div>
        <div className={rowClass(layout)}>
          <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
            {t('detail.required', 'Обязательность')}
          </dt>
          <dd className={valueClass(layout, requiredChanged)}>
            {min === undefined
              ? dash
              : min >= 1
                ? t('detail.yes', 'Да')
                : t('detail.no', 'Нет')}
          </dd>
        </div>
      </div>
    </div>
  );
}

/** One option (`Вариант N`): localized names in boxes plus the price row. */
function ModifierOptionBlock({
  option,
  peer,
  index,
  highlight,
  t,
  layout,
}: {
  option: ModifierOption;
  peer: ModifierOption | undefined;
  index: number;
  highlight: boolean;
  t: Translate;
  layout: ModifiersRowLayout;
}) {
  const priceChanged = highlight && (peer === undefined || peer.price !== option.price);
  const unit = typeof option.unit_code === 'string' ? option.unit_code : '';
  const peerUnit = typeof peer?.unit_code === 'string' ? peer.unit_code : '';
  const unitChanged = highlight && unit !== peerUnit;

  // All three locales always render (empty reads as `—`).
  const bodyText = rowsTextClass();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm leading-[1.4] font-semibold text-black dark:text-foreground">
        {t('detail.variant', 'Вариант {{n}}').replace('{{n}}', String(index + 1))}
      </p>
      {LOCALE_ORDER.map(({ key, labelKey, fallback }) => {
        const value = localeLine(option.name ?? undefined, key);
        const changed = highlight && value !== localeLine(peer?.name ?? undefined, key);
        return (
          <div key={key} className={`${rowClass(layout)} ${bodyText} leading-[1.4]`}>
            <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
              {t(labelKey, fallback)}
            </dt>
            <dd className={valueClass(layout, changed)}>{value === '' ? '—' : value}</dd>
          </div>
        );
      })}
      <div className={`${rowClass(layout)} ${bodyText} leading-[1.4] font-normal`}>
        <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
          {t('detail.price', 'Цена')}
        </dt>
        <dd className={`${valueClass(layout, priceChanged)} tabular-nums`}>
          {formatMoney(option.price)}
        </dd>
      </div>
      <div className={`${rowClass(layout)} ${bodyText} leading-[1.4] font-normal`}>
        <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
          {t('detail.unit', 'Единица измерения')}
        </dt>
        <dd className={valueClass(layout, unitChanged)}>
          {formatFieldValue('unit_code', unit, t)}
        </dd>
      </div>
    </div>
  );
}
